import {
  CSRF_TOKEN_HEADER,
  SERVER_API_PATH,
  ServerApiDetailResponseType,
  reportApiError,
  serverApi,
} from "./api-server-actions";

import {
  AISchemaId,
  AISchemaRequestOptionsType,
  AISchemaResponseTypesById,
  AISchemasById,
  AIServerRequestOptionsType,
  AIStreamingRequestOptionsType,
  SSETextDeltaDataType,
  SSEToolCallDataType,
} from "components/server-types";
import { t } from "i18n/i18n.ts";
import { getCsrfToken } from "utils/auth";

/**
 * Perform an AI query to get a JSON response, using a specified schema to constrain
 * the expected response format. Queries are performed synchronously.
 */
export async function serverGetAISchemaResponse<T extends AISchemaId>(
  options: AISchemaRequestOptionsType<T>,
): Promise<AISchemaResponseTypesById[T]> {
  // Issue the request to run the given query and obtain a JSON response.
  const url = `${SERVER_API_PATH}/ai/chat/`;
  const schema = AISchemasById[options.schemaId];
  const data: AIServerRequestOptionsType = {
    section_id: options.section_id,
    model: options.model,
    prompt: options.prompt,
    files: options.files || [],
    schema,
  };
  type ResponseType = { type: "json"; result: AISchemaResponseTypesById[T] };
  const response = await serverApi.post<ResponseType>(url, data);

  // Check for errors.
  if (response.data.type !== "json") {
    throw new Error(`serverGetAISchemaResponse: Invalid response type: ${response.data.type}`);
  }

  // Process the response.
  return response.data?.result;
}

/**
 * Perform an AI query to get a response in the form of an event stream, where each event may
 * be either a delta (i.e., a chunk of text) or a tool call. In theory deltas and tool calls
 * can be intermingled, but in practice a stream will consist of only one or the other.
 */
export async function serverGetAIEventStreamResponse(
  options: AIStreamingRequestOptionsType,
): Promise<void> {
  // Issue the request to run the given query and obtain a streaming response.
  const url = serverApi.getUri({ url: `${SERVER_API_PATH}/ai/chat/` });
  const data: AIServerRequestOptionsType = {
    section_id: options.section_id,
    model: options.model,
    prompt: options.prompt,
    files: options.files || [],
    tools: options.tools,
    stream: true,
  };
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      [CSRF_TOKEN_HEADER]: getCsrfToken(),
    },
    body: JSON.stringify(data),
  });

  // Check for errors.
  if (!response.ok || !response.body) {
    throw new Error(await response.text());
  }

  // The response will come as a stream of data that can be accessed by way of a reader
  // object. Each chunk of data yielded by the reader will be a byte array that has to
  // be decoded to UTF-8 before processing.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let emptyResponse = true;

  return new Promise<void>((resolve, reject) => {
    // This handler function gets called repeatedly to parse and process SSE messages from
    // each chunk of the streaming response as it becomes availabe from the reader object.
    // After it processes the available data, it invokes the reader again with itself as
    // the callback to continue the cycle. When no more data is available, it will resolve
    // the promise.
    function processChunk(result: ReadableStreamReadResult<Uint8Array>): void | Promise<void> {
      const { value } = result;

      // If any data is available from the stream, process it and repeat the cycle.
      if (value) {
        const chunk = decoder.decode(value as AllowSharedBufferSource, { stream: true });
        for (const message of chunk.trim().split("\n\n")) {
          processServerSentEvent(message, options);
        }
        emptyResponse = false;
        return reader.read().then(processChunk).catch(reject);
      }

      // If no data is available, handle potential errors and then terminate the cycle.
      if (emptyResponse) {
        options.onTextDelta(t("error.toasts.get_ai_response_failure"));
      }
      resolve();
    }

    // Initiate the cycle.
    reader.read().then(processChunk).catch(reject);
  });
}

/**
 * Internal helper function to process one SSE message from a streaming response.
 * Messages take the form of a newline-separated block of text containing an event
 * type and data. Each message will come in one of the following formats:
 *
 * 1. event: text_delta
 *    data: { "delta": "..." }
 *
 * 2. event: function_call
 *    data: { "name": "...", "arguments": {...} }
 *
 */
function processServerSentEvent(message: string, options: AIStreamingRequestOptionsType) {
  const lines = message.split(/\n/);
  const event = lines[0].slice("event:".length).trim();
  const rawData = JSON.parse(lines[1].slice("data:".length).trim());
  // Uncomment for debugging:
  // console.info(`processServerSentEvent: received event=${event}, data=${JSON.stringify(rawData)}`);
  if (event === "text_delta") {
    const data = rawData as SSETextDeltaDataType;
    options.onTextDelta(data.delta);
  }
  if (event === "function_call") {
    const calls = rawData as SSEToolCallDataType[];
    options.onToolCalls(calls);
  }
}

/**
 * Tell the server to refresh its Gitbook-based RAG retrievers. Admin-only.
 */
export async function serverRefreshGitbook(): Promise<ServerApiDetailResponseType> {
  try {
    const response = await serverApi.post<ServerApiDetailResponseType>(
      `${SERVER_API_PATH}/ai/refresh-gitbook/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverRefreshGitbook", error);
    throw error;
  }
}
